import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Clock, MapPin, Check, X } from "lucide-react";
import { useT } from "../lib/i18n";
import GalleryLightbox from "../components/GalleryLightbox";

export default function TourDetail() {
  const T = useT();
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [form, setForm] = useState({ customer_name:"", customer_mobile:"", customer_whatsapp:"", customer_email:"", passengers:2, notes:"" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [lightbox, setLightbox] = useState({open:false, index:0});

  useEffect(()=>{ api.get(`/tours/${id}`).then(r=>setT(r.data)).catch(()=>{}); }, [id]);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/tours/enquire", { tour_id: id, ...form, passengers: parseInt(form.passengers)||1 });
      setDone(r.data);
      toast.success("Enquiry sent! We'll call you soon.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Enquiry failed"); }
    setLoading(false);
  };
  if (!t) return <PublicLayout><div className="max-w-4xl mx-auto p-10">Loading…</div></PublicLayout>;

  return (
    <PublicLayout>
      <section className="max-w-6xl mx-auto px-4 pt-10 pb-16 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          <div className="rounded-2xl overflow-hidden relative cursor-pointer hover-zoom" onClick={()=> (t.gallery_urls?.length ? setLightbox({open:true, index:0}) : null)} data-testid="tour-poster-clickable">
            <img src={t.poster_url} className="w-full aspect-[4/3] object-cover" alt={t.title} />
            {t.badge && <div className="absolute top-4 left-4 bg-[#ea580c] text-white text-xs px-3 py-1.5 rounded-full font-semibold">{t.badge}</div>}
            {t.gallery_urls?.length > 0 && (
              <div className="absolute bottom-4 right-4 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full font-semibold">
                📷 {t.gallery_urls.length + 1} photos · Tap to view
              </div>
            )}
          </div>
          {t.gallery_urls?.length > 0 && (
            <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="tour-gallery-thumbs">
              {t.gallery_urls.slice(0, 6).map((u, i) => (
                <button key={i} onClick={()=>setLightbox({open:true, index:i+1})} className="aspect-square overflow-hidden rounded-lg hover-zoom" data-testid={`gallery-thumb-${i}`}>
                  <img src={u} alt={`gallery ${i+1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <div className="text-xs uppercase tracking-widest text-[#ea580c] font-semibold mt-6">{t.subtitle}</div>
          <h1 className="text-3xl md:text-4xl font-display font-black tracking-tight mt-1">{t.title}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-slate-600">
            <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4 text-[#ea580c]" /> {t.days}D · {t.nights}N</span>
            <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4 text-[#ea580c]" /> {t.destination}</span>
            {t.starts_on && <span>Departs {t.starts_on}</span>}
          </div>
          {t.stay_details && <div className="mt-4 p-4 rounded-xl bg-slate-50 border border-slate-200 text-sm"><b>Stay:</b> {t.stay_details}</div>}
          {t.highlights?.length>0 && (
            <div className="mt-6">
              <div className="font-display font-bold text-lg">{T("tour.highlights")}</div>
              <ul className="mt-2 grid grid-cols-2 gap-2 text-sm">{t.highlights.map((h,i)=><li key={i} className="inline-flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5" /> {h}</li>)}</ul>
            </div>
          )}
          {t.itinerary?.length>0 && (
            <div className="mt-6">
              <div className="font-display font-bold text-lg">{T("tour.itinerary")}</div>
              <ol className="mt-2 space-y-2 text-sm">{t.itinerary.map((h,i)=><li key={i} className="pl-4 border-l-2 border-[#ea580c] py-1">{h}</li>)}</ol>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-6 mt-6">
            {t.inclusions?.length>0 && <div><div className="font-display font-bold">{T("tour.inclusions")}</div><ul className="mt-2 text-sm space-y-1">{t.inclusions.map((i,x)=><li key={`inc-${i.slice(0,12)}-${x}`} className="inline-flex items-start gap-2 w-full"><Check className="w-4 h-4 text-emerald-600 mt-0.5" /> {i}</li>)}</ul></div>}
            {t.exclusions?.length>0 && <div><div className="font-display font-bold">Exclusions</div><ul className="mt-2 text-sm space-y-1">{t.exclusions.map((i,x)=><li key={x} className="inline-flex items-start gap-2 w-full"><X className="w-4 h-4 text-red-500 mt-0.5" /> {i}</li>)}</ul></div>}
          </div>
        </div>

        <aside className="lg:col-span-2">
          <div className="sticky top-24 p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
            <div className="text-xs text-slate-500">Per person</div>
            <div className="text-4xl font-display font-black text-[#1e1b4b]">₹{t.price_per_person.toLocaleString()}</div>
            {t.child_price && <div className="text-sm text-slate-500">Child: ₹{t.child_price.toLocaleString()}</div>}
            {t.seats_available!=null && <div className="mt-2 text-sm text-emerald-600 font-medium">{t.seats_available} seats available</div>}
            {done ? (
              <div className="mt-4 p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-sm">
                <div className="font-semibold text-emerald-900">Enquiry received!</div>
                <div className="mt-1">Booking ID: <b>{done.booking_id}</b>. Our team will contact you shortly on WhatsApp.</div>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-4 space-y-3" data-testid="tour-enquiry-form">
                <div><Label>Your Name</Label><Input required value={form.customer_name} onChange={e=>setForm({...form,customer_name:e.target.value})} data-testid="enq-name" /></div>
                <div><Label>Mobile</Label><Input required value={form.customer_mobile} onChange={e=>setForm({...form,customer_mobile:e.target.value})} data-testid="enq-mobile" /></div>
                <div><Label>WhatsApp</Label><Input value={form.customer_whatsapp} onChange={e=>setForm({...form,customer_whatsapp:e.target.value})} placeholder="Same as mobile if empty" data-testid="enq-whatsapp" /></div>
                <div><Label>Email (optional)</Label><Input type="email" value={form.customer_email} onChange={e=>setForm({...form,customer_email:e.target.value})} data-testid="enq-email" /></div>
                <div><Label>Passengers</Label><Input type="number" min="1" value={form.passengers} onChange={e=>setForm({...form,passengers:e.target.value})} data-testid="enq-pax" /></div>
                <div><Label>Notes</Label><Textarea value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="Any special requests?" data-testid="enq-notes" /></div>
                <Button disabled={loading} type="submit" className="w-full bg-[#ea580c] hover:bg-orange-700" data-testid="enq-submit">{loading?"Sending…":"Send Enquiry"}</Button>
              </form>
            )}
          </div>
        </aside>
      </section>
      {lightbox.open && (
        <GalleryLightbox
          images={[t.poster_url, ...(t.gallery_urls || [])].filter(Boolean)}
          startIndex={lightbox.index}
          onClose={()=>setLightbox({open:false, index:0})}
        />
      )}
    </PublicLayout>
  );
}
